/**
 * "New credential" pre-modal — pick the credential TYPE first, then the form.
 *
 * The flat row of ~12 type chips on the form doesn't scale and buries the choice.
 * This chooser opens first: a searchable, category-grouped grid of boxes. Picking
 * one closes the chooser and the parent opens `CredentialDialog` pre-set to that
 * type. Grouping comes from the backend `category` on each type, so a new type
 * self-places (mirrors `SourceKindChooser`).
 */
import { useQuery } from "@tanstack/react-query";
import {
  Database,
  Mail,
  Send,
  KeyRound,
  Key,
  Shield,
  FolderUp,
  Sparkles,
  Mic,
  Loader2,
  Lock,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { credentialsApi, type CredentialType } from "@/lib/api-credentials";
import { cn } from "@/lib/cn";

/** Per-type icon (falls back to a lock for any unmapped/new type). */
const TYPE_ICON: Record<string, LucideIcon> = {
  postgres: Database,
  mysql: Database,
  sqlserver: Database,
  smtp: Mail,
  telegram_bot: Send,
  http_bearer: KeyRound,
  api_key: Key,
  hmac: Shield,
  sftp: FolderUp,
  llm_provider: Sparkles,
  voice_provider: Mic,
};

/** Category render order + heading. An unknown category falls through to "other". */
const CATEGORY_ORDER = ["database", "messaging", "http", "file", "ai", "other"] as const;
const CATEGORY_LABEL: Record<string, [string, string]> = {
  database: ["credentials.chooser.cat.database", "Database"],
  messaging: ["credentials.chooser.cat.messaging", "Messaggistica"],
  http: ["credentials.chooser.cat.http", "HTTP & sicurezza"],
  file: ["credentials.chooser.cat.file", "Trasferimento file"],
  ai: ["credentials.chooser.cat.ai", "AI"],
  other: ["credentials.chooser.cat.other", "Altro"],
};

export interface CredentialTypeChooserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired with the picked type id; the parent opens the form for it. */
  onPick: (typeId: CredentialType) => void;
}

export function CredentialTypeChooser({ open, onOpenChange, onPick }: CredentialTypeChooserProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");

  const typesQ = useQuery({
    queryKey: ["credentials", "types"],
    queryFn: credentialsApi.types,
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const groups = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = (typesQ.data ?? []).filter(
      (ty) =>
        !q ||
        ty.label.toLowerCase().includes(q) ||
        ty.description.toLowerCase().includes(q) ||
        ty.type_id.toLowerCase().includes(q),
    );
    const byCat = new Map<string, typeof matched>();
    for (const ty of matched) {
      const cat = CATEGORY_LABEL[ty.category] ? ty.category : "other";
      (byCat.get(cat) ?? byCat.set(cat, []).get(cat)!).push(ty);
    }
    // Stable category order; only non-empty groups.
    return CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => ({
      cat: c,
      label: CATEGORY_LABEL[c],
      items: byCat.get(c)!,
    }));
  }, [typesQ.data, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {t("credentials.chooser.title", "Nuova credenziale")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("credentials.chooser.search", "Cerca un tipo di connessione…")}
            autoFocus
            type="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            // Password managers (1Password/LastPass/Bitwarden) sometimes inject on
            // a focused field — these opt them out too.
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
          />

          {typesQ.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
            </div>
          ) : groups.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {t("credentials.chooser.empty", "Nessun tipo corrisponde alla ricerca.")}
            </p>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.cat} className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(g.label[0], g.label[1])}
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {g.items.map((ty) => {
                      const Icon = TYPE_ICON[ty.type_id] ?? Lock;
                      return (
                        <button
                          key={ty.type_id}
                          type="button"
                          onClick={() => onPick(ty.type_id)}
                          className={cn(
                            "group flex items-start gap-2.5 rounded-lg border border-border bg-surface p-3 text-left",
                            "transition-colors hover:border-accent/60 hover:bg-accent/5",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                          )}
                        >
                          <Icon
                            className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-accent"
                            strokeWidth={1.5}
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{ty.label}</div>
                            <div className="text-xs text-muted-foreground">{ty.description}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
